import type { ColorMode } from '~/design-system/components/ColorWheel/ColorWheel';
import type { GradientStop } from '~/design-system/elements/ColorPicker/ColorPicker';

/**
 * Shared CSS gradient (de)serialization for the color picker and any consumer that needs to render or
 * round-trip a gradient. Kept in one place so the swatch preview, the rendered `background`, and the
 * editor all agree on the exact CSS string (no logic fork).
 */

/** A hex (#rrggbb) + 0–100 opacity → an `rgba(...)` string. Falls back to opaque black on bad input. */
export const toRgba = (hex: string, op: number): string => {
  if (!hex || hex === 'transparent' || !hex.startsWith('#') || hex.length < 7) {
    return `rgba(0,0,0,${op / 100})`;
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return `rgba(0,0,0,${op / 100})`;
  return `rgba(${r},${g},${b},${op / 100})`;
};

/** Serialize gradient stops + mode into a CSS gradient string (linear = left→right, radial = circle,
 *  angular = conic). The single source of truth for how a gradient fill becomes CSS. */
export const stopsToGradientCss = (stops: GradientStop[], mode: ColorMode): string => {
  const sorted = [...stops].sort((a, b) => a.position - b.position);
  const colorStops = sorted
    .map((s) => `${toRgba(s.color, s.opacity ?? 100)} ${s.position}%`)
    .join(', ');
  if (mode === 'radial') return `radial-gradient(circle, ${colorStops})`;
  if (mode === 'angular') return `conic-gradient(${colorStops})`;
  return `linear-gradient(to right, ${colorStops})`;
};

/** Split a comma-separated argument list at top level only (commas inside `rgb(...)`/`rgba(...)` etc. are
 *  not separators). */
const splitTopLevel = (input: string): string[] => {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of input) {
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ',' && depth === 0) {
      parts.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) parts.push(current);
  return parts;
};

/** Parse a single color token (`#rrggbb`, `#rgb`, `rgb(...)`, `rgba(...)`) → `{ hex, opacity }`, or null
 *  when the token is not a color (e.g. a `to right` / `45deg` / `circle` direction token). */
const parseColorToken = (token: string): { hex: string; opacity: number } | null => {
  const t = token.trim();
  const rgba = t.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/i);
  if (rgba) {
    const r = parseInt(rgba[1], 10);
    const g = parseInt(rgba[2], 10);
    const b = parseInt(rgba[3], 10);
    const a = rgba[4] !== undefined ? parseFloat(rgba[4]) : 1;
    const hex = `#${[r, g, b].map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('')}`;
    return { hex, opacity: Math.round(a * 100) };
  }
  const hex6 = t.match(/^#([0-9a-f]{6})$/i);
  if (hex6) return { hex: `#${hex6[1].toLowerCase()}`, opacity: 100 };
  const hex3 = t.match(/^#([0-9a-f]{3})$/i);
  if (hex3) {
    const [r, g, b] = hex3[1].split('');
    return { hex: `#${r}${r}${g}${g}${b}${b}`.toLowerCase(), opacity: 100 };
  }
  return null;
};

let parseStopId = 0;

const norm360 = (deg: number) => ((deg % 360) + 360) % 360;

// `to <side(s)>` keywords → CSS gradient degrees, keyed by alphabetically-sorted sides.
const TO_SIDE_ANGLE: Record<string, number> = {
  top: 0,
  right: 90,
  bottom: 180,
  left: 270,
  'right top': 45,
  'bottom right': 135,
  'bottom left': 225,
  'left top': 315,
};

/** Parse a gradient direction token (`45deg`, `0.25turn`, `to top right`, conic `from 120deg at …`) →
 *  CSS degrees, or null. */
const parseAngleToken = (token: string): number | null => {
  const t = token.trim().toLowerCase();
  // Conic start angle: `from <n>deg` (possibly followed by an `at <pos>` the position parser handles).
  const from = t.match(/\bfrom\s+(-?[\d.]+)deg\b/);
  if (from) return norm360(parseFloat(from[1]));
  const deg = t.match(/^(-?[\d.]+)deg$/);
  if (deg) return norm360(parseFloat(deg[1]));
  const turn = t.match(/^(-?[\d.]+)turn$/);
  if (turn) return norm360(parseFloat(turn[1]) * 360);
  const rad = t.match(/^(-?[\d.]+)rad$/);
  if (rad) return norm360((parseFloat(rad[1]) * 180) / Math.PI);
  const to = t.match(/^to\s+(.+)$/);
  if (to) {
    const key = to[1].split(/\s+/).sort().join(' ');
    return key in TO_SIDE_ANGLE ? TO_SIDE_ANGLE[key] : null;
  }
  return null;
};

const POSITION_KEYWORD: Record<string, number> = { left: 0, top: 0, center: 50, right: 100, bottom: 100 };

const coordToPercent = (c: string): number | null => {
  if (c in POSITION_KEYWORD) return POSITION_KEYWORD[c];
  const m = c.match(/^(-?[\d.]+)%$/);
  return m ? Math.max(0, Math.min(100, parseFloat(m[1]))) : null;
};

/** Parse a radial/conic centre token (`at 30% 70%`, `at center`) → `{ x, y }` percent, or null. */
const parsePositionToken = (token: string): { x: number; y: number } | null => {
  const at = token.trim().toLowerCase().match(/\bat\s+(.+)$/);
  if (!at) return null;
  const coords = at[1].trim().split(/\s+/);
  const x = coordToPercent(coords[0]);
  if (x == null) return null;
  const y = coords[1] != null ? coordToPercent(coords[1]) : 50;
  return y == null ? null : { x, y };
};

/** Best-effort inverse of {@link stopsToGradientCss}: parse a CSS gradient string back into editable
 *  `{ mode, stops, angle?, position? }`, or null when it isn't a recognizable gradient (caller then falls
 *  back to solid editing while keeping the original string for render — no data loss). Non-color tokens
 *  (the leading direction/shape) yield the axis angle + centre; color tokens become stops. */
export const gradientCssToStops = (
  css: string | undefined,
): {
  mode: ColorMode
  stops: GradientStop[]
  angle?: number
  position?: { x: number; y: number }
  radius?: number
} | null => {
  if (typeof css !== 'string') return null;
  const trimmed = css.trim();
  const mode: ColorMode = /^radial-gradient/i.test(trimmed)
    ? 'radial'
    : /^conic-gradient/i.test(trimmed)
      ? 'angular'
      : /^(?:repeating-)?linear-gradient/i.test(trimmed)
        ? 'linear'
        : 'solid';
  if (mode === 'solid') return null;
  const open = trimmed.indexOf('(');
  const close = trimmed.lastIndexOf(')');
  if (open < 0 || close <= open) return null;

  const parsed: { color: string; opacity: number; position: number | null }[] = [];
  let angle: number | undefined;
  let position: { x: number; y: number } | undefined;
  let radius: number | undefined;
  for (const part of splitTopLevel(trimmed.slice(open + 1, close))) {
    const segment = part.trim();
    if (!segment) continue;
    const posMatch = segment.match(/(-?[\d.]+)%\s*$/);
    const stopPos = posMatch ? Math.max(0, Math.min(100, parseFloat(posMatch[1]))) : null;
    const colorPart = posMatch ? segment.slice(0, segment.length - posMatch[0].length) : segment;
    const color = parseColorToken(colorPart);
    if (!color) {
      // Config token (direction / shape / centre / size) → recover axis angle, centre + radius, then skip.
      const a = parseAngleToken(segment);
      if (a != null) angle = a;
      const p = parsePositionToken(segment);
      if (p) position = p;
      const r = segment.match(/(?:circle|ellipse)\s+(-?[\d.]+)px/i);
      if (r) radius = parseFloat(r[1]);
      continue;
    }
    parsed.push({ color: color.hex, opacity: color.opacity, position: stopPos });
  }
  if (parsed.length < 2) return null;

  const stops: GradientStop[] = parsed.map((s, i) => ({
    id: `pstop-${++parseStopId}`,
    position: s.position ?? Math.round((i / (parsed.length - 1)) * 100),
    color: s.color,
    opacity: s.opacity,
  }));
  return { mode, stops, angle, position, radius };
};
